import React, { Component } from "react";
import { hasBecomeTrue } from "~/hocs/crud";
import { messages } from "../../i18n-messages";
import { injectIntl, InjectedIntlProps } from "@ai360/core/intl";
import { model, service } from "../data";
import {
    getAgBytesErrorClassNames,
    handlePicklistChange,
    mapToPicklistValue,
    memoizeSelectableOptions,
    onNumberChange,
    onTextChange,
    prepareSelectableOptions,
} from "~/admin/utils";

// Components
import {
    Checkbox,
    NumericInput,
    Section,
    SelectInput,
    SubSection,
    TextArea,
    TextInput,
} from "~/core";
import {
    PICKLIST_BRAND_ORGANIZATION,
    PICKLIST_HERBICIDE_TOLERANCE,
} from "~/core/picklist/picklist-names";
import TraitEvents from "../components/trait-events";
import TraitGenes from "../components/trait-gene";
import { adminData, GUID, ID, NAME, PICKLIST_GUID, VALUE } from "~/admin/data";
// Style
import "./add-edit-panel.css";
// Types
import { IAddEditProps, IAddEditPanelState, TraitGene } from "../interfaces";

export class AddEditPanel extends Component<IAddEditProps & InjectedIntlProps, IAddEditPanelState> {
    trait: TraitGene;

    getBrandOrganizationOptions = memoizeSelectableOptions((options) =>
        prepareSelectableOptions(options, { guid: PICKLIST_GUID, label: VALUE, id: ID })
    );
    getHerbicideToleranceOptions = memoizeSelectableOptions((options) =>
        prepareSelectableOptions(options, { guid: PICKLIST_GUID, label: VALUE, id: ID })
    );
    getCropOptions = memoizeSelectableOptions((options) =>
        prepareSelectableOptions(options, {
            guid: GUID,
            label: NAME,
            id: ID,
            appendIdToLabel: true,
        })
    );

    constructor(props) {
        super(props);
        this.trait = props.record || {};
        this.state = {
            nextId: null,
        };
    }

    componentDidMount() {
        this.setupTraitRecord();
        this.props.setBreadcrumbs([""]);
        const { needs } = this.props;
        needs([
            this.props.getNextId(),
            this.props.fetchPicklistData(),
            this.props.fetchDropdownData(),
        ]);

        if (this.props.recordGuid) {
            needs([this.props.fetchRecord(this.props.recordGuid)]);
        }
    }

    componentDidUpdate(prevProps) {
        if (hasBecomeTrue("fetchData", prevProps, this.props)) {
            this.props.liftRecordData(this.trait);
        }
        if (this.props.addEditPanel.mode === "ADD") {
            this.setupNextId(this.props);
        } else {
            if (this.props.record && this.props.record !== prevProps.record) {
                this.trait = this.props.record;
                this.setHeaderText();
            }
        }
    }

    setupNextId = (props) => {
        if (props.nextId && props.nextId !== this.state.nextId) {
            this.trait[model.PROPS_TRAIT_ID] = "" + props.nextId;
            this.setState({
                nextId: props.nextId,
            });
        }
    };

    setupTraitRecord = () => {
        const { addEditPanel } = this.props;
        this.trait = this.props.record || {};
        if (addEditPanel.mode === "ADD") {
            this.trait = service.getDefaultRecord();
        }
    };

    onPicklistChange = ({ type, guid }, value, callback?: () => void) => {
        this.trait = handlePicklistChange(this.trait, { type, guid, value }, callback, true);
    };

    onTextChange = (formKey, value, callback?: () => void) => {
        if (formKey === model.PROPS_TRAIT_EVENTS || formKey === model.PROPS_TRAIT_GENES) {
            const valueArr = [];
            value.forEach((val) => {
                for (const prop in val) {
                    valueArr.push(val[prop]);
                }
            });
            this.trait = onTextChange(
                this.trait,
                { formKey: [formKey], value: valueArr },
                callback
            );
        } else {
            this.trait = onTextChange(this.trait, { formKey: [formKey], value }, callback);
        }
    };

    onNumberChange = (formKey, value, callback?: () => void) => {
        this.trait = onNumberChange(this.trait, { formKey: [formKey], value }, callback);
    };

    setHeaderText = () => {
        this.props.setBreadcrumbs([this.trait[model.PROPS_TRAIT_NAME]]);
    };

    renderTraitInfo = () => {
        const { formatMessage } = this.props.intl;
        const { trait } = this;
        return (
            <div className="section-container trait-section">
                <div className="section-container section-column trait-sub-section">
                    <Section testId="trait-general-section">
                        <SubSection testId="trait-trait-id-subsection">
                            <NumericInput
                                tabIndex={0}
                                scale={0}
                                precision={9}
                                suppressFormatting
                                containerClassNames={getAgBytesErrorClassNames(
                                    [187, 188],
                                    this.props.apiErrors,
                                    ["form-input-id"]
                                )}
                                placeholderText={formatMessage(messages.traitId)}
                                labelText={formatMessage(messages.traitId)}
                                value={trait[model.PROPS_TRAIT_ID]}
                                onChange={(value) =>
                                    this.onNumberChange(model.PROPS_TRAIT_ID, value)
                                }
                                required
                                testId="trait-trait-id-input"
                            />
                            {!this.props.userRole[model.PROPS_ACTIVE_INACTIVE] ? null : (
                                <Checkbox
                                    className="active-checkbox"
                                    value={trait[model.PROPS_ACTIVE_YN]}
                                    label={formatMessage(messages.active)}
                                    onChange={(value) =>
                                        this.onTextChange(model.PROPS_ACTIVE_YN, value)
                                    }
                                    testId="trait-is-active-checkbox"
                                />
                            )}
                        </SubSection>
                        <SubSection
                            className="trait-container"
                            testId="trait-gmo-and-name-subsection"
                        >
                            <Checkbox
                                value={trait[model.PROPS_TRAIT_GMO]}
                                label={formatMessage(messages.gmo)}
                                autoFocus={true}
                                onChange={(e, value) =>
                                    this.onTextChange(model.PROPS_TRAIT_GMO, value)
                                }
                                testId="trait-gmo-checkbox"
                            />
                            <TextInput
                                tabIndex={0}
                                maxLength={50}
                                containerClassNames={getAgBytesErrorClassNames(
                                    [185, 186],
                                    this.props.apiErrors
                                )}
                                placeholderText={formatMessage(messages.traitName)}
                                value={trait[model.PROPS_TRAIT_NAME] || ""}
                                onChange={(value) =>
                                    this.onTextChange(
                                        model.PROPS_TRAIT_NAME,
                                        value,
                                        this.setHeaderText
                                    )
                                }
                                required
                                testId="trait-trait-name-input"
                            />
                        </SubSection>
                    </Section>
                </div>
                <div className="section-container section-column trait-section-align trait-sub-section">
                    <Section testId="trait-abbreviation-and-brand-section">
                        <SubSection
                            className="form-section-row-2"
                            testId="trait-abbreviation-and-brand-subsection"
                        >
                            <TextInput
                                tabIndex={0}
                                maxLength={25}
                                placeholderText={formatMessage(messages.abbreviation)}
                                value={trait[model.PROPS_TRAIT_ABBREVIATION] || ""}
                                onChange={(value) =>
                                    this.onTextChange(model.PROPS_TRAIT_ABBREVIATION, value)
                                }
                                testId="trait-abbreviation-input"
                            />
                            <SelectInput
                                tabIndex={0}
                                optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                                options={this.getBrandOrganizationOptions(
                                    this.props[PICKLIST_BRAND_ORGANIZATION]
                                )}
                                placeholderText={formatMessage(messages.brand)}
                                value={mapToPicklistValue({
                                    options: this.getBrandOrganizationOptions(
                                        this.props[PICKLIST_BRAND_ORGANIZATION]
                                    ),
                                    selectedGuid: trait[model.PROPS_TRAIT_BRAND_ORGANIZATION_GUID],
                                })}
                                onChange={(value) => {
                                    this.onPicklistChange(
                                        {
                                            type: model.PROPS_TRAIT_BRAND_ORGANIZATION,
                                            guid: model.PROPS_TRAIT_BRAND_ORGANIZATION_GUID,
                                        },
                                        value
                                    );
                                }}
                                testId="trait-brand-select"
                            />
                        </SubSection>
                    </Section>
                </div>
                <div className="section-container section-column trait-section-align trait-sub-section">
                    <Section testId="trait-herbicide-tolerance-section">
                        <SubSection
                            className="form-section-row-2"
                            testId="trait-herbicide-tolerance-subsection"
                        >
                            <SelectInput
                                tabIndex={0}
                                optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                                options={this.getHerbicideToleranceOptions(
                                    this.props[PICKLIST_HERBICIDE_TOLERANCE]
                                )}
                                placeholderText={formatMessage(messages.herbicideTolerance)}
                                value={mapToPicklistValue({
                                    options: this.getHerbicideToleranceOptions(
                                        this.props[PICKLIST_HERBICIDE_TOLERANCE]
                                    ),
                                    selectedGuid: trait[model.PROPS_HERBICIDE_TOLERANCE_GUID],
                                })}
                                onChange={(value) => {
                                    this.onPicklistChange(
                                        {
                                            type: model.PROPS_HERBICIDE_TOLERANCE,
                                            guid: model.PROPS_HERBICIDE_TOLERANCE_GUID,
                                        },
                                        value
                                    );
                                }}
                                testId="trait-herbicide-tolerance-select"
                            />
                        </SubSection>
                    </Section>
                </div>
            </div>
        );
    };
    renderDetailInfo1() {
        const { formatMessage } = this.props.intl;
        return (
            <div className="section-container section-column">
                <Section
                    headerText={formatMessage(messages.refugeDetails)}
                    testId="trait-refuge-details-section"
                >
                    <SubSection testId="trait-crop-and-refuge-requirements-subsection">
                        <SelectInput
                            tabIndex={0}
                            containerClassNames={getAgBytesErrorClassNames(
                                140,
                                this.props.apiErrors
                            )}
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            options={this.getCropOptions(this.props[model.PROPS_CROP_NAME])}
                            placeholderText={formatMessage(messages.cropIdName)}
                            required
                            value={mapToPicklistValue({
                                options: this.getCropOptions(this.props[model.PROPS_CROP_NAME]),
                                selectedGuid: this.trait[model.PROPS_CROP_GUID],
                            })}
                            onChange={(value) => {
                                this.onPicklistChange(
                                    {
                                        type: model.PROPS_CROP_NAME,
                                        guid: model.PROPS_CROP_GUID,
                                    },
                                    value
                                );
                            }}
                            testId="trait-crop-select"
                        />
                        <TextInput
                            tabIndex={0}
                            maxLength={400}
                            placeholderText={formatMessage(messages.refugeRequirements)}
                            value={this.trait[model.PROPS_REFUGE_REQUIREMENTS] || ""}
                            onChange={(value) =>
                                this.onTextChange(model.PROPS_REFUGE_REQUIREMENTS, value)
                            }
                            testId="trait-refuge-requirements-input"
                        />
                    </SubSection>

                    <SubSection testId="trait-notes-subsection">
                        <TextArea
                            maxLength={4000}
                            placeholderText={formatMessage(messages.notes)}
                            value={this.trait[model.PROPS_NOTES || ""]}
                            onChange={(value) => this.onTextChange(model.PROPS_NOTES, value)}
                            testId="trait-notes-textarea"
                        />
                    </SubSection>
                </Section>
            </div>
        );
    }

    renderDetailInfo2() {
        const { formatMessage } = this.props.intl;
        return (
            <div className="section-container section-column">
                <Section
                    className="grid-section"
                    headerText={formatMessage(messages.traitGene, { count: 0 })}
                    testId="trait-trait-gene-section"
                >
                    <SubSection testId="trait-trait-gene-subsection">
                        <TraitGenes
                            formatMessage={formatMessage}
                            options={this.props[model.PROPS_TRAIT_GENES]}
                            record={(this.trait[model.PROPS_TRAIT_GENES] || []).map((geneName) => ({
                                geneName,
                            }))}
                            itemListAlias={model.PROPS_TRAIT_GENES}
                            onTextChange={(e, value) =>
                                this.onTextChange(model.PROPS_TRAIT_GENES, value)
                            }
                        />
                    </SubSection>
                </Section>
            </div>
        );
    }

    renderDetailInfo3() {
        const { formatMessage } = this.props.intl;
        return (
            <div className="section-container section-column">
                <Section
                    className="grid-section"
                    headerText={formatMessage(messages.traitEvent, {
                        count: 0,
                    })}
                    testId="trait-trait-event-section"
                >
                    <SubSection testId="trait-trait-event-subsection">
                        <TraitEvents
                            formatMessage={formatMessage}
                            record={(this.trait[model.PROPS_TRAIT_EVENTS] || []).map(
                                (eventName) => ({ eventName })
                            )}
                            itemListAlias={model.PROPS_TRAIT_EVENTS}
                            onTextChange={this.onTextChange}
                        />
                    </SubSection>
                </Section>
            </div>
        );
    }
    render() {
        return (
            <div className="add-edit-panel">
                {this.renderTraitInfo()}
                <div className="section-container">
                    {this.renderDetailInfo1()}
                    <span className="bar section-fence"></span>
                    {this.renderDetailInfo2()}
                    <span className="bar section-fence"></span>
                    {this.renderDetailInfo3()}
                </div>
            </div>
        );
    }
}

export default injectIntl(AddEditPanel);
